"use client";

import IconBox from "@/components/common/IconBox";
import Button from "@/components/ui/Button";
import { ScrollLockCard } from "@/components/ui/HorizontalScrollCards";
import { useScroll, useSpring, useTransform, motion } from "framer-motion";
import { useRef } from "react";
import Link from "next/link";
import { IoSettingsOutline } from "react-icons/io5";
import { VscPackage } from "react-icons/vsc";
import { GoChecklist } from "react-icons/go";
import { MobileStackCard, FeatureCard } from "./FeatureCard";

const features = [
  {
    title: "Supplying",
    description:
      "Once you have your Georgia Lottery COAM Class B license, we supply the latest, newest, and highest-earning skill games available for the Georgia market.",
    icon: VscPackage,
    image: "/Supplying.svg",
  },
  {
    title: "License Approval",
    description:
      "Getting your GA Lottery COAM Location Class B License is a hassle. Don’t want to deal with the stress of finding rules and regulations by yourself?",
    icon: GoChecklist,
    image: "/License.svg",
    showBadge: true,
  },
  {
    title: "Set-Up",
    description:
      "Our team of technicians will bring you the latest and best titles for an expert set-up. This includes a quality-assurance check and expert advice if you have questions or concerns.",
    icon: IoSettingsOutline,
    image: "/Setup.svg",
  },
];

// Mobile Scroll Stack Component
function MobileScrollStack() {
  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  const cardCount = features.length;
  // Adjusted height calculation for better spacing
  const trackHeight = `${200 + cardCount * 30}vh`;

  return (
    <div className="bg-brand-black md:hidden">
      {/* Header Section - Scrolls normally */}
      <section className="bg-brand-black relative flex w-full flex-col items-center justify-center px-5 pt-10 pb-0">
        <div className="flex w-full max-w-[1200px] flex-col items-center justify-center text-center">
          {/* Icon */}
          <div className="mb-2.5">
            <IconBox icon={IoSettingsOutline} />
          </div>

          {/* Subtitle */}
          <p className="font-raleway mb-2.5 text-sm font-normal text-gray-300">
            Want to introduce Coin Operated Amusement Machines (COAM) to your
            business?
          </p>

          {/* Title */}
          <h2 className="font-raleway mb-2.5 text-2xl font-bold text-white">
            Let Us Take Care Of Your Installation, Service, And Licensing Needs!
          </h2>

          {/* Description */}
          <p className="font-raleway mb-6 max-w-5xl text-base font-normal text-gray-300">
            Halcyon Amusements carries the latest coin operated amusement
            machines (COAM) on the market. Browse our inventory and find the
            perfect fit for your business. When it comes to skill games, we have
            options for establishments and clientele of all kind.
          </p>

          {/* Button */}
          <Link href="/services">
            <Button variant="gradient">Know More</Button>
          </Link>
        </div>
      </section>

      {/* Cards Section - Scroll stack */}
      <section
        ref={containerRef}
        className="bg-brand-black relative w-full"
        style={{ height: trackHeight }}
      >
        <div className="sticky top-0 flex h-screen w-full items-center justify-center px-5 pt-10">
          {/* Cards Stack */}
          <div className="relative h-[200px] w-full max-w-[400px]">
            {features.map((feature, index) => (
              <MobileStackCard
                key={index}
                feature={feature}
                index={index}
                totalCards={cardCount}
                scrollYProgress={smoothProgress}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default function FeatureSection() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  // Header opacity - fades out BEFORE cards appear
  const headerOpacity = useTransform(smoothProgress, [0, 0.15], [1, 0]);

  // Delay the card animation start until progress is > 0.05
  const cardScrollProgress = useTransform(smoothProgress, [0.05, 1], [0, 1]);

  return (
    <>
      {/* Mobile Version: Scroll stack animation with rotation */}
      <MobileScrollStack />

      {/* Desktop Version: Sticky Head + Horizontal Scroll Cards */}
      <section
        ref={containerRef}
        className="bg-brand-black relative hidden md:block"
        style={{ height: "600vh" }}
      >
        <div className="sticky top-0 flex h-screen w-full items-center justify-center overflow-hidden">
          {/* Header - Fades out BEFORE cards appear */}
          <motion.div
            style={{ opacity: headerOpacity }}
            className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center px-20 text-center"
          >
            {/* Icon */}
            <div className="mb-2.5">
              <IconBox icon={IoSettingsOutline} />
            </div>

            {/* Subtitle */}
            <p className="font-raleway mb-2.5 text-base font-normal text-gray-300">
              Want to introduce Coin Operated Amusement Machines (COAM) to your
              business?
            </p>

            {/* Title */}
            <h2 className="font-raleway mb-2.5 text-[2rem] leading-tight font-bold text-white">
              Let Us Take Care Of Your Installation, Service, And Licensing
              Needs!
            </h2>

            {/* Description */}
            <p className="font-raleway mb-10 max-w-5xl text-base font-normal text-gray-300">
              Halcyon Amusements carries the latest coin operated amusement
              machines (COAM) on the market. Browse our inventory and find the
              perfect fit for your business. When it comes to skill games, we
              have options for establishments and clientele of all kind.
            </p>

            {/* Button */}
            <Link href="/services" className="pointer-events-auto">
              <Button variant="gradient">Know More</Button>
            </Link>
          </motion.div>

          {/* Cards Container - Using ScrollLockCard for Right-to-Left animation */}
          <div className="relative flex w-full flex-row items-center justify-center">
            {features.map((feature, index) => (
              <ScrollLockCard
                key={index}
                index={index}
                totalCards={features.length}
                scrollProgress={cardScrollProgress}
                className="shrink-0"
                yOffset={index === 1 ? -20 : 0}
              >
                <FeatureCard feature={feature} index={index} />
              </ScrollLockCard>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
